import React from 'react';
import styled from 'styled-components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

/**
 * T2-6194: one consistent Talent back control, placed in the page BODY at the
 * far left (just under the navbar logo) — not inside the navbar. Replaces the
 * per-page variants (outlined pill / icon-only / none / next-to-logo) so every
 * Talent sub-page (Job Board, My Stats, Invoices, Job Details) shows the same
 * back button in the same spot. Matches the existing AvailablePositions look.
 */

const Wrap = styled.div<{ $background?: string }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  // Match the page body background so the back-button row never shows a forced
  // white strip above a coloured body (T2-6194). Transparent by default so it
  // inherits whatever container it's mounted in.
  background: ${({ $background }) => $background || 'transparent'};
  // Align the pill's left edge under the navbar logo (header padding 12px).
  padding: 0.75rem 12px 0;
  box-sizing: border-box;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  color: var(--primary-blue, #0B4598);
  font-family: var(--fontMain, Poppins), sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background: rgba(11, 69, 152, 0.04);
    border-color: var(--primary-blue, #0B4598);
  }

  &:active {
    transform: scale(0.98);
  }
`;

interface TalentBackButtonProps {
  onBack: () => void;
  label?: string;
  /** Optional background to match the page body when mounted above a coloured container. */
  background?: string;
}

const TalentBackButton: React.FC<TalentBackButtonProps> = ({ onBack, label = 'Back', background }) => (
  <Wrap $background={background}>
    <BackBtn onClick={onBack} className="no-fastclick" aria-label="Back">
      <ArrowBackIcon />
      <span>{label}</span>
    </BackBtn>
  </Wrap>
);

export default TalentBackButton;
